/* eslint-disable no-console */
import { formatAddress } from 'Feature/V2/HbtPortfolioManagement/utils/FormatAddress';
import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { formatRatio } from 'Feature/V2/HbtPortfolioManagement/utils/FormatRatio';
import i18n from 'i18next';
import { forEach } from 'lodash-es';
import {
  Borrower,
  CmhcLowRatioDataLoanDetails,
  KeyLoanDetails,
  LenderDetails,
  LoanDetails,
  OtherLoanDetails,
  PropertyLoanDetails,
  RequestDetails
} from '../types';

interface HbtIndividualLoanDetailsMapperType {
  borrowerLoanDetails: Borrower[];
  requestDetails: RequestDetails;
  lenderDetails: LenderDetails;
  propertyDetails: PropertyLoanDetails;
  keyLoanDetails: KeyLoanDetails;
  cmhcLowRatioDetails: CmhcLowRatioDataLoanDetails;
  otherLoanData: OtherLoanDetails;
}

export const HbtIndividualLoanDetailsMapper = (
  data: LoanDetails
): HbtIndividualLoanDetailsMapperType => {
  const borrowerLoanDetails: Borrower[] = data.borrowers.map((b) => {
    const name = b?.firstName
      ? `${b?.lastName ?? ''}, ${b?.firstName ?? ''}`
      : `${b?.lastName ?? ''}`;
    return {
      name,
      dateOfBirth: b?.dateOfBirth || '',
      address: formatAddress(b.address),
      creditScore: b.creditScore || '',
      totalIncome: formatAmountWithDollar(b.totalIncome?.toString() || '', i18n.language)
    };
  });

  const requestDetails: RequestDetails = {
    portfolioId: data.portfolioId.toString(),
    insuredApprovedLenderName: data.insureApprovedLender,
    underwritingApprovedLenderName: data.underwritingApprovedLender
  };

  const lenderDetails: LenderDetails = {
    insuredApprovedLenderName: data.underwritingApprovedLender,
    underwritinApprovedLenderName: data.underwritingApprovedLender
  };

  const propertyDetails: PropertyLoanDetails = {
    address: formatAddress(data.propertyAddress),
    locationType: data.propertyUrbanRuralIndicator,
    occupancyType: data.propertyOccupancyType,
    tenureType: data.propertyTenureType,
    noOfUnits: data.propertyNoOfUnits.toString(),
    dwellingType: data.propertyDwellingType,
    legalDescription: data.propertyLegalDescription
  };

  const keyLoanDetails: KeyLoanDetails = {
    originalLendingValue: formatAmountWithDollar(
      data.loanOriginalLendingValue?.toString() || '',
      i18n.language
    ),
    originalLoanAmount: formatAmountWithDollar(
      data.loanOriginalAmount?.toString() || '',
      i18n.language
    ),
    originalLTVRatio: formatRatio(
      Number(data.loanOriginalLtvRatio?.toString() || ''),
      i18n.language
    ),
    currentLoanAmount: formatAmountWithDollar(
      data.loanCurrentAmount?.toString() || '',
      i18n.language
    ),
    currentLTVRatio: formatRatio(Number(data.loanCurrentLtvRatio?.toString() || ''), i18n.language),
    interestAdjustmentDate: data.loanInterestAdjustmentDate || '',
    loanTerm: data.loanTerm,
    loanTermExpiryDate: data.loanTermExpiryDate || '',
    interestType: data.loanInterestType,
    grandfathered: data.loanGrandfatheredIndicator,
    loanPurposeType: data.loanPurposeType || '',
    qualifyingTotalDebtService: data.loanQualifyingTotalDebtService?.toString() || '',
    qualifyingGrossDebtService: data.loanQualifyingGrossDebtService?.toString() || '',
    originalAmortization: data.loanOriginalAmortization?.toString() || '',
    remainingAmortization: data.loanRemainingAmortization?.toString() || '',
    paymentResetFrequency: data.loanPaymentResetFrequency?.toString() || '',
    interestRate: data.loanCurrentInterestRate?.toString() || ''
  };

  let coBorrower = {
    dateOfBirth: '',
    creditScore: '',
    risk: ''
  };

  let borrower = {
    dateOfBirth: '',
    creditScore: '',
    risk: ''
  };

  if (Array.isArray(data.borrowers)) {
    for (let i = 0; i < data.borrowers.length; i++) {
      if (data.borrowers[i].typeId === 1) {
        borrower.creditScore = data.borrowers[i].creditScore || '';
        borrower.dateOfBirth = data.borrowers[i].dateOfBirth || '';
        borrower.risk = data.borrowers[i].risk || '';
      }

      if (data.borrowers[i].typeId === 2) {
        coBorrower.creditScore = data.borrowers[i].creditScore || '';
        coBorrower.dateOfBirth = data.borrowers[i].dateOfBirth || '';
        coBorrower.risk = data.borrowers[i].risk || '';
      }
    }
  }

  const cmhcLowRatioDetails: CmhcLowRatioDataLoanDetails = {
    underwritingLenderName: data.lowRatioUnderwritingLender ?? { englishName: '', frenchName: '' },
    accountNumber: data.lowRatioCmhcAccountNumber ? String(data.lowRatioCmhcAccountNumber) : '',
    lastReferedDate: data.lastReferredDate || '',
    lowRatioCmhcAccountNumber: data.lowRatioCmhcAccountNumber,
    lowRatioResults: data.lowRatioResults || '',
    lowRatioAge: data.lowRatioAge?.toString() || '',
    applicationRisk: data.lowRatioApplicationRisk || '',
    propertyRisk: data.lowRatioPropertyRisk || '',
    borrowerRisk: data.borrowerRisk || '',
    coborrowerRisk: data.coBorrowerRisk || '',
    borrowerDoB: data.borrowerBirthDate || '',
    coborrowerDoB: data.coBorrowerBirthDate || '',
    borrowerCreditScore: data.borrowerCreditScore || '',
    coborrowerCreditScore: data.coBorrowerCreditScore || '',
    propertyAddress: formatAddress(data.lowRatioPropertyAddress),
    noOfUnits: data.propertyNoOfUnits.toString(),
    dwellingType: data.lowRatioDwellingType || '',
    tenureType: data.lowRatioTenureType || '',
    originalLoanAmount: formatAmountWithDollar(
      data.lowRatioOriginalLoanAmount?.toString() || '',
      i18n.language
    ),
    originalLendingValue: formatAmountWithDollar(
      data.lowRatioOriginalLendingValue?.toString() || '',
      i18n.language
    ),
    loanPurposeType: data.lowRatioLoanPurposeType || ''
  };
  const otherLoanData: OtherLoanDetails = {
    multiComponentKey: data.otherMultiComponentKey,
    mortgageSecurityPriorityRanking: data.otherMortgageSecurityPriorityRanking?.toString() || '',
    mortgageSecurityType: data.mortgageSecurityType,
    propertyValuationMethod: data.propertyValuationMethod,
    propertyValuationMethodOther: data.propertyValuationMethodOtherDescription,
    mortgatePaymentFrequency: data.mortgagePaymentFrequency,
    mortgatePrincipalAndInterestPaymentAmount: formatAmountWithDollar(
      data.mortgagePrincipalAndInterestPaymentAmountNoTax?.toString() || '',
      i18n.language
    ),
    residentialStatus: data.residentialStatus,
    propertyListingType: data.propertyListingType,
    sourceOfBusiness: data.sourceOfBusiness || '',
    brokerMortgageSpecialistName: `${data.brokerMortgageSpecialistFirstName ?? ''} ${data.brokerMortgageSpecialistLastName ?? ''}`
  };

  return {
    borrowerLoanDetails,
    requestDetails,
    lenderDetails,
    propertyDetails,
    keyLoanDetails,
    cmhcLowRatioDetails,
    otherLoanData
  };
};
